import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';
import PriceSimulator from './components/PriceSimulator';
import DeliverySimulator from './components/DeliverySimulator';
import BookingForm from './components/BookingForm';

// SEO Component for booking page
function BookingSEO() {
  useEffect(() => {
    // Update page title
    document.title = '料金シミュレーター | 岐阜県美濃加茂市洗車コーティング専門店 Garage de carcare';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岐阜県美濃加茂市の洗車コーティング専門店Garage de carcareの料金シミュレーターページ。車種と状態に合わせた最適なプランと料金をシミュレーション。土日限定・完全予約制。');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/booking`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "料金シミュレーター",
      "description": "岐阜県美濃加茂市の洗車コーティング専門店Garage de carcareの料金シミュレーターページ",
      "url": `${import.meta.env.VITE_SITE_URL}/booking`,
      "mainEntity": {
        "@type": "Service",
        "name": "洗車コーティングサービス料金シミュレーター",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Garage de carcare"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return null;
}

// Header component for booking page
function BookingHeader() {
  const navigate = useNavigate();

  const handleNavigation = (section: string) => {
    if (section === 'home') {
      navigate('/');
    } else {
      navigate('/', { state: { scrollTo: section } });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={() => handleNavigation('home')}
              className="cursor-pointer"
            >
              <img 
                src="https://static.readdy.ai/image/18ddd3c5386ce7455627992c42f8b0cb/f4e0fe60f5ceffe9412c875da26f8438.jpeg"
                alt="ガレージ・ド・カルカレ ロゴ"
                className="h-12 w-auto object-contain"
              />
            </button>
          </div>

          {/* Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => handleNavigation('home')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              ホーム
            </button>
            <button 
              onClick={() => handleNavigation('about')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              概要
            </button>
            <button 
              onClick={() => handleNavigation('services')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              サービス・料金
            </button>
            <button 
              onClick={() => handleNavigation('gallery')}
              className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              施工実績
            </button>
            <button 
              onClick={() => navigate('/reserve')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              予約する
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button className="text-gray-700 hover:text-blue-600">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default function BookingPage() {
  const [selectedCourse, setSelectedCourse] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState(0);

  return (
    <div className="min-h-screen bg-gray-50">
      <BookingSEO />
      <BookingHeader />
      
      <div className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-4xl font-bold mb-4">料金シミュレーター</h1>
            <p className="text-xl text-blue-100">
              お客様の車種と状態に合わせた最適なプランをご提案いたします
            </p>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Price Simulator */}
            <PriceSimulator 
              onCourseSelect={setSelectedCourse}
              onPriceUpdate={setEstimatedPrice}
            />

            {/* Delivery Simulator */}
            <DeliverySimulator 
              onPriceUpdate={() => {}}
            />
          </div>

          {/* CTA Section */}
          <div className="mt-16">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* 通常予約ボタン */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-car-washing-line text-blue-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                  店舗での洗車・コーティング
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  研磨なし・軽研磨・2層コーティングなど、本格的なコーティングをご希望の方
                </p>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/reserve')}
                  className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap text-lg"
                >
                  <i className="ri-calendar-check-line mr-2"></i>
                  店舗予約ページへ進む
                </button>
              </div>

              {/* 出張洗車予約ボタン */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-truck-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                  出張洗車サービス
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  ご自宅や職場までお伺いする出張洗車をご希望の方
                </p>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/delivery-reserve')}
                  className="w-full bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap text-lg"
                >
                  <i className="ri-truck-line mr-2"></i>
                  出張洗車予約ページへ進む
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
